import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent, ListComponent, SearchResult } from 'src/app/shared';
import {
  InventoryAttachment,
  InventoryItem,
} from '../../models/inventory/inventory-item';
import {
  formatInventoryFileSize,
  InventoryAttachmentExtensions,
  InventoryLimits,
  MaxAttachmentBytes,
} from '../../models/inventory/inventory-limits';
import { createInventoryTranslationRequest } from '../../models/inventory/inventory-translation-request';
import { emulator } from '../../services/emulator.service';
import { InventoryService } from '../../services/inventory/inventory.service';

export interface InventoryAddAttachmentsDialogData {
  item: InventoryItem;
  source: string;
  claimId: string;
  lob: string;
  claim: SearchResult;
}

@Component({
  selector: 'app-inventory-add-attachments-dialog',
  template: `
    <app-dialog
      title="Add attachments"
      ariaLabel="Add attachments dialog"
      confirmLabel="Save"
      cancelLabel="Cancel"
      [width]="700"
      [confirmDisable]="!hasAttachedFiles"
      [confirmLoading]="uploading"
      (confirmEvent)="save($event)"
      [showHeader]="true"
    >
      <div class="app-pb2">
        {{
          'Please upload your attachments for the item below. Up to 10 files can be selected. The following formats are supported: .jpg, .png, .webp, .pdf, .rtf, .tif, .txt, .docx, .heic, .heics, .heif, .heifs. Size limit is 45MB per file.'
            | translation: translationRequest
        }}
      </div>

      <div class="app-pb2">
        <span class="tw-font-bold">{{
          'Item:' | translation: translationRequest
        }}</span>
        {{ data.item.itemName }}
      </div>

      <input
        type="file"
        class="file-input"
        [accept]="supportedExtensionsAccept"
        (change)="fileSelected($event)"
        (click)="fileUpload.value = null"
        #fileUpload
        multiple
      />

      <app-action-row class="app-pb2">
        <app-button
          left1
          emphasis="High"
          icon="upload"
          label="Upload"
          [disabled]="uploading"
          (click)="fileUpload.click()"
        ></app-button>
      </app-action-row>

      @if (pendingFiles.length > 0) {
        <app-list
          [dataSource]="pendingFiles"
          [template]="row"
          [filterEnabled]="false"
          [paginatorEnabled]="false"
          [includeNoDataIcon]="false"
          [loaded]="true"
          [tableAriaLabel]="'Attachments'"
          #uploadTable
        >
          <ng-container header>
            <tr class="tw-flex tw-flex-wrap tw-w-full">
              <app-list-header
                name="fileName"
                label="Document"
                class="tw-w-full md:tw-w-[40%]"
              ></app-list-header>
              <app-list-header
                name="status"
                label="Status"
                readOnly
                class="tw-w-full md:tw-w-[22%]"
              ></app-list-header>
              <app-list-header
                name="fileSize"
                label="File size"
                readOnly
                class="tw-w-full md:tw-w-[18%]"
              ></app-list-header>
              <app-list-header
                label="Remove"
                readOnly
                class="tw-w-full md:tw-w-[20%]"
              ></app-list-header>
            </tr>
          </ng-container>
        </app-list>
      }

      <ng-template #row let-element let-index="index">
        <tr class="tw-flex tw-flex-wrap tw-w-full tw-items-start">
          <td class="tw-w-full md:tw-w-[40%] attachment-cell">
            <div class="attachment-name" [attr.title]="element.fileName">
              <span class="attachment-label md:tw-hidden">
                {{ 'Document' | translation: translationRequest }}:
              </span>
              <span class="attachment-name-text">{{ element.fileName }}</span>
            </div>
          </td>
          <td class="tw-w-full md:tw-w-[22%] attachment-cell">
            <span
              class="attachment-status"
              [class.status-error]="element.status === 'error'"
              [class.status-attached]="element.status === 'attached'"
            >
              {{ getStatusDisplay(element) | translation: translationRequest }}
            </span>
          </td>
          <td class="tw-w-full md:tw-w-[18%] attachment-cell">
            <span class="attachment-label md:tw-hidden">
              {{ 'File size' | translation: translationRequest }}:
            </span>
            <span class="attachment-name-text">{{
              getFileSizeDisplay(element)
            }}</span>
          </td>
          <td class="tw-w-full md:tw-w-[20%] attachment-cell">
            <app-icon-button
              icon="delete_outline"
              label="Remove"
              tabindex="0"
              role="button"
              ariaLabelText="Remove attachment"
              [disabled]="uploading"
              (click)="removeFile(index)"
              (keydown.enter)="removeFile(index)"
            ></app-icon-button>
          </td>
        </tr>
      </ng-template>
    </app-dialog>
  `,
  styles: [
    `
      .file-input {
        display: none;
      }
      .attachment-cell {
        min-width: 0;
        padding-top: 4px;
        padding-bottom: 4px;
      }
      .attachment-name,
      .attachment-name-text,
      .attachment-status {
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .attachment-name-text {
        line-height: 16px;
        color: var(--color-slate-600);
      }
      .attachment-label {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        line-height: 16px;
        padding-right: 4px;
      }
      .attachment-status {
        display: inline-block;
        max-width: 100%;
      }
      .status-error {
        color: var(--sed-status-red-color);
      }
      .status-attached {
        color: var(--color-green);
      }
    `,
  ],
  standalone: false,
})
export class InventoryAddAttachmentsDialogComponent extends BaseComponent {
  @ViewChild('uploadTable')
  uploadTable: ListComponent;

  pendingFiles: InventoryAttachment[] = [];
  hasAttachedFiles = false;
  uploading = false;

  private readonly maxFiles = InventoryLimits.maxAttachmentsPerItem;
  private readonly extensions: readonly string[] =
    InventoryAttachmentExtensions;

  supportedExtensionsAccept = this.extensions.join(',');

  translationRequest = createInventoryTranslationRequest();

  constructor(
    public dialogRef: MatDialogRef<InventoryAddAttachmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InventoryAddAttachmentsDialogData,
    private inventoryService: InventoryService,
    private emulatorService: emulator,
  ) {
    super();
  }

  fileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const existingCount = this.data.item.attachments?.length ?? 0;

    for (const file of Array.from(input.files)) {
      const attachment: InventoryAttachment = {
        fileName: file.name,
        dateUploaded: new Date(),
        status: 'attached',
        file,
        fileSize: file.size,
      };

      const parts = file.name.split('.');
      const ext = '.' + parts[parts.length - 1].toLowerCase();
      const totalPendingAttached = this.pendingFiles.filter(
        (f) => f.status === 'attached',
      ).length;

      if (existingCount + totalPendingAttached >= this.maxFiles) {
        attachment.status = 'error';
        attachment.statusMessage = 'Error: Maximum file count is 10';
        attachment.file = undefined;
      } else if (!file.size) {
        attachment.status = 'error';
        attachment.statusMessage = 'Error: File content is missing';
        attachment.file = undefined;
      } else if (file.size > MaxAttachmentBytes) {
        attachment.status = 'error';
        attachment.statusMessage = `Error: Maximum file size is ${InventoryLimits.maxAttachmentMegabytes} MB`;
        attachment.file = undefined;
      } else if (
        this.pendingFiles.some(
          (f) =>
            f.file?.size === file.size &&
            f.file?.name === file.name &&
            f.file?.lastModified === file.lastModified,
        )
      ) {
        attachment.status = 'error';
        attachment.statusMessage = 'Error: Duplicate file found';
        attachment.file = undefined;
      } else if (!this.extensions.includes(ext)) {
        attachment.status = 'error';
        attachment.statusMessage = 'Error: Unsupported file type';
        attachment.file = undefined;
      }

      this.pendingFiles = [...this.pendingFiles, attachment];
    }

    this.updateSaveState();
    this.uploadTable?.updateDataSource();
  }

  removeFile(index: number): void {
    this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index);
    this.updateSaveState();
    this.uploadTable?.updateDataSource();
  }

  getStatusDisplay(attachment: InventoryAttachment): string {
    if (attachment.status === 'error') {
      return attachment.statusMessage ?? 'Error';
    }
    return 'Attached';
  }

  getFileSizeDisplay(attachment: InventoryAttachment): string {
    return formatInventoryFileSize(attachment.fileSize ?? attachment.file?.size);
  }

  async save(event: string): Promise<void> {
    if (event !== 'confirm' || this.uploading) {
      return;
    }

    const attachedFiles = this.pendingFiles.filter(
      (f) => f.status === 'attached',
    );
    if (!attachedFiles.length) {
      return;
    }

    // Auditors emulating a claimant are preview-only and must not upload on their behalf.
    if (this.emulatorService.showAuditorInfo()) {
      this.dialogRef.close();
      return;
    }

    this.uploading = true;
    try {
      const uploaded: InventoryAttachment[] = [];

      for (const attachment of attachedFiles) {
        if (!attachment.file?.size) {
          attachment.status = 'error';
          attachment.statusMessage = 'Error: File content is missing';
          continue;
        }

        const response = await this.inventoryService.uploadAttachment(
          this.data.source,
          this.data.claimId,
          this.data.lob,
          attachment.file,
          this.data.item.id,
          this.data.claim,
        );

        if (!response?.success || !response.attachmentId) {
          attachment.status = 'error';
          attachment.statusMessage =
            response?.statusDescription || 'Error: Upload failed';
          continue;
        }

        uploaded.push({
          attachmentId: response.attachmentId,
          fileName: response.fileName || attachment.fileName,
          dateUploaded: response.dateUploaded
            ? new Date(response.dateUploaded)
            : new Date(),
          status: 'attached',
          fileSize:
            response.fileSize ?? attachment.fileSize ?? attachment.file?.size,
        });
      }

      this.pendingFiles = [...this.pendingFiles];
      this.uploadTable?.updateDataSource();

      if (!uploaded.length) {
        this.updateSaveState();
        return;
      }

      this.dialogRef.close(uploaded);
    } finally {
      this.uploading = false;
    }
  }

  private updateSaveState(): void {
    this.hasAttachedFiles = this.pendingFiles.some(
      (f) => f.status === 'attached',
    );
  }
}
